/**
 * Category utilities for deriving and managing categories from exercises
 */

import { sortCategoriesWithPredefined } from './categoryOrdering';

/**
 * Derives categories with counts from an array of exercises
 * @param {Array} exercises - Array of exercise objects
 * @returns {Array} Array of category objects with name and exercise_count
 */
export const deriveCategoriesFromExercises = (exercises) => {
  if (!exercises || exercises.length === 0) {
    return [{ name: 'All Categories', exercise_count: 0 }];
  }

  // Filter out exercises with invalid categories
  const validExercises = exercises.filter(exercise => {
    const category = exercise.category;
    return category &&
           typeof category === 'string' &&
           category.trim() !== '';
  });

  // Extract unique categories and count exercises
  const categoryMap = new Map();
  validExercises.forEach(exercise => {
    const category = exercise.category;
    categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
  });

  // Convert to array format
  const categoriesWithCounts = Array.from(categoryMap.entries())
    .map(([name, exercise_count]) => ({ name, exercise_count }));

  // Sort using predefined order (Python, Linear Algebra, ML, DL, NLP, then alphabetical)
  const sortedCategories = sortCategoriesWithPredefined(categoriesWithCounts);

  // Add "All Categories" option at the beginning
  const allCategoriesOption = {
    name: 'All Categories',
    exercise_count: validExercises.length
  };

  return [allCategoriesOption, ...sortedCategories];
};

/**
 * Extracts unique categories from exercises array, sorted alphabetically
 * @param {Array} exercises - Array of exercise objects
 * @returns {Array} Array of unique category names
 */
export const extractUniqueCategories = (exercises) => {
  if (!exercises || exercises.length === 0) {
    return [];
  }

  const uniqueCategories = new Set();

  exercises.forEach(exercise => {
    const category = exercise.category;
    if (category &&
        typeof category === 'string' &&
        category.trim() !== '') {
      uniqueCategories.add(category);
    }
  });

  return Array.from(uniqueCategories).sort();
};

/**
 * Calculates the number of exercises in a specific category
 * @param {Array} exercises - Array of exercise objects
 * @param {string} categoryName - Name of the category to count
 * @returns {number} Number of exercises in the category
 */
export const calculateCategoryCount = (exercises, categoryName) => {
  if (!exercises || exercises.length === 0 || !categoryName) {
    return 0;
  }

  return exercises.filter(exercise => {
    const category = exercise.category;
    return category &&
           typeof category === 'string' &&
           category.trim() !== '' &&
           category === categoryName;
  }).length;
};

/**
 * Gets a formatted display name for a category
 * @param {string} categoryName - Category name to format
 * @returns {string} Formatted display name
 */
export const getCategoryDisplayName = (categoryName) => {
  if (!categoryName || typeof categoryName !== 'string') {
    return '';
  }

  // Handle already formatted names
  if (categoryName.includes(' ') && categoryName[0] === categoryName[0].toUpperCase()) {
    return categoryName;
  }

  // Convert kebab-case, snake_case, and lowercase to Title Case
  return categoryName
    .replace(/[-_]/g, ' ')
    .split(' ')
    .map(word => {
      if (word.length === 0) return word;
      // Handle special cases like AI, ML
      if (word.toLowerCase() === 'ai' || word.toLowerCase() === 'ml') {
        return word.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};